import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import salesService from "../../../utils/salesService";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Icon from "../../../components/AppIcon";

const DataEntryForm = ({ onSubmit, onAutoSave, editingEntry }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    company_id: "",
    entry_date: new Date().toISOString().split("T")[0],
    sales_amount: "",
    collection_amount: "",
    outstanding_amount: "",
    notes: "",
  });

  // Load companies on component mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        company_id: editingEntry.company_id || "",
        entry_date:
          editingEntry.entry_date || new Date().toISOString().split("T")[0],
        sales_amount: editingEntry.sales_amount?.toString() || "",
        collection_amount: editingEntry.collection_amount?.toString() || "",
        outstanding_amount: editingEntry.outstanding_amount?.toString() || "",
        notes: editingEntry.notes || "",
      });
    }
  }, [editingEntry]);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (onAutoSave && (formData.sales_amount || formData.collection_amount)) {
        onAutoSave({
          ...formData,
          user_id: user?.id,
        });
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, onAutoSave, user?.id]);

  const loadCompanies = async () => {
    try {
      // console.log("user", user);

      setLoadingCompanies(true);
      const result = await salesService.getCompanies(user.id);

      if (result?.success) {
        setCompanies(result.data || []);
      } else {
        setError(result?.error || "Failed to load companies");
      }
    } catch (error) {
      setError("Failed to load companies");
      console.log("Load companies error:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-calculate outstanding amount
    if (field === "sales_amount" || field === "collection_amount") {
      const sales =
        field === "sales_amount"
          ? parseFloat(value) || 0
          : parseFloat(formData.sales_amount) || 0;
      const collection =
        field === "collection_amount"
          ? parseFloat(value) || 0
          : parseFloat(formData.collection_amount) || 0;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
        outstanding_amount: Math.max(0, sales - collection).toString(),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const entryData = {
        ...formData,
        user_id: user?.id,
        sales_amount: parseFloat(formData.sales_amount) || 0,
        collection_amount: parseFloat(formData.collection_amount) || 0,
        outstanding_amount: parseFloat(formData.outstanding_amount) || 0,
        status: "submitted",
      };

      let result;
      if (editingEntry) {
        result = await salesService.updateSalesEntry(
          editingEntry.id,
          entryData
        );
      } else {
        result = await salesService.createSalesEntry(entryData);
      }

      if (result?.success) {
        // Reset form if creating new entry
        if (!editingEntry) {
          setFormData({
            company_id: "",
            entry_date: new Date().toISOString().split("T")[0],
            sales_amount: "",
            collection_amount: "",
            outstanding_amount: "",
            notes: "",
          });
        }

        // Call parent callback
        if (onSubmit) {
          await onSubmit(result.data);
        }
      } else {
        setError(result?.error || "Failed to save entry");
      }
    } catch (error) {
      setError("Failed to save entry");
      console.log("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateCollectionRate = () => {
    const sales = parseFloat(formData.sales_amount) || 0;
    const collection = parseFloat(formData.collection_amount) || 0;

    if (sales > 0) {
      return ((collection / sales) * 100).toFixed(1);
    }
    return "0.0";
  };

  const companyOptions = companies.map((company) => ({
    value: company.id,
    label: `${company.name} (${company.code})`,
  }));

  if (loadingCompanies) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <Icon
            name="Loader2"
            size={24}
            className="animate-spin text-primary"
          />
          <span className="ml-2 text-muted-foreground">Loading form...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {editingEntry ? "Edit Sales Entry" : "New Sales Entry"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter daily sales figures with validation
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Save" size={16} />
            <span>Auto-save enabled</span>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Company *
              </label>
              <Select
                value={formData.company_id}
                onChange={(value) => handleInputChange("company_id", value)}
                required
                options={companyOptions}
              ></Select>
            </div>

            {/* Entry Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Entry Date *
              </label>
              <Input
                type="date"
                value={formData.entry_date}
                onChange={(e) =>
                  handleInputChange("entry_date", e.target.value)
                }
                required
              />
            </div>

            {/* Sales Amount */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Sales Amount *
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.sales_amount}
                onChange={(e) =>
                  handleInputChange("sales_amount", e.target.value)
                }
                placeholder="0.00"
                required
              />
            </div>

            {/* Collection Amount */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Collection Amount *
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.collection_amount}
                onChange={(e) =>
                  handleInputChange("collection_amount", e.target.value)
                }
                placeholder="0.00"
                required
              />
            </div>

            {/* Outstanding Amount (Auto-calculated) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Outstanding Amount
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.outstanding_amount}
                onChange={(e) =>
                  handleInputChange("outstanding_amount", e.target.value)
                }
                placeholder="0.00"
                className="bg-muted"
                readOnly
              />
              <p className="text-xs text-muted-foreground mt-1">
                Auto-calculated: Sales - Collection
              </p>
            </div>

            {/* Collection Rate (Display only) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Collection Rate
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  value={`${calculateCollectionRate()}%`}
                  className="bg-muted"
                  readOnly
                />
                <div className="text-sm text-muted-foreground">
                  {parseFloat(calculateCollectionRate()) >= 80 ? (
                    <Icon
                      name="TrendingUp"
                      size={16}
                      className="text-success"
                    />
                  ) : parseFloat(calculateCollectionRate()) >= 60 ? (
                    <Icon name="Minus" size={16} className="text-warning" />
                  ) : (
                    <Icon
                      name="TrendingDown"
                      size={16}
                      className="text-destructive"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Additional notes or comments..."
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              * Required fields
            </div>
            <div className="flex items-center space-x-3">
              {editingEntry && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={
                  isLoading || !formData.company_id || !formData.sales_amount
                }
                iconName={isLoading ? "Loader2" : "Save"}
                iconPosition="left"
              >
                {isLoading
                  ? "Saving..."
                  : editingEntry
                  ? "Update Entry"
                  : "Submit Entry"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DataEntryForm;
